import { z } from 'zod'

import type { OepTargetFileJob } from 'enrollment-platform/pages/CustomerManagement/pages/ViewCustomer/tabs/OpenEnrollment/types'

import { annualBenefitPlanningApi } from '../../../shared/infra/api/annual-benefit-planning.api'
import { contributionModelApi } from '../../../shared/infra/api/contribution-model.api'

/**
 * OEP configuration document API (EMS-7683 — Customer Configuration › Open
 * Enrollment tab; EMS-10154 — list/apply, FR-013 action model). Wraps the
 * presign / confirm / delete / list / apply endpoints for the analysis XLSX
 * so the presentation layer never calls the HTTP client directly. Mirrors
 * approval.api.ts: EPS wraps every response as { status: 'ok', data: <payload> }
 * (buildResponse), so each schema unwraps the envelope before validating.
 */

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/**
 * Unwrap the EPS { status: 'ok', data } envelope, then validate with `schema`.
 * `data: null` is a valid payload (e.g. "no latest job yet"), so the envelope is
 * detected by the presence of the key — not by `data` being a non-null object.
 */
function withEnvelope<T extends z.ZodTypeAny>(schema: T) {
  return z.preprocess((raw) => {
    if (!isRecord(raw)) return raw
    return raw.status === 'ok' && 'data' in raw ? raw.data : raw
  }, schema)
}

export interface OepPresignDto {
  readonly presignedUrl: string
  readonly uploadKey: string
}

export interface OepDocumentDto {
  readonly documentId: string
  readonly filename: string
  // ISO 8601 upload timestamp; null if absent.
  readonly uploadedAt: string | null
}

const PresignSchema = withEnvelope(
  z.object({
    presignedUrl: z.string().min(1),
    uploadKey: z.string().min(1),
  })
)

/** confirm returns { documentId, version, filename, uploadedAt } — documentId required. */
const ConfirmDocumentSchema = withEnvelope(
  z.object({
    documentId: z.string().min(1),
    filename: z.string().min(1),
    uploadedAt: z.string().min(1).nullable().catch(null),
  })
)

// EMS-10185 User Story 2 — derived from the broker_background_jobs ledger for
// the analysis_file_uploaded job; 'completed' both when the job finishes and
// when no job is found at all (pre-ticket documents, fail open). 'failed'
// added under EMS-10154 (2026-09-08) so a stuck upload is distinguishable
// from one still in progress — CANCELLED still folds into 'processing'.
export type OepDocumentProcessingStatus = 'processing' | 'completed' | 'failed'

/** One problem upload validation found in an analysis file. */
export interface AnalysisFileIssueDto {
  readonly sheet: string
  // Worksheet row number as Excel shows it; null for a whole-sheet problem.
  readonly row: number | null
  readonly column: string | null
  readonly message: string
}

export interface AnalysisFileValidationIssuesDto {
  readonly totalCount: number
  // The first problems found — capped server-side, so may be shorter than totalCount.
  readonly issues: readonly AnalysisFileIssueDto[]
}

// Template the analysis file was built from (EMS-11022). Only ABP-5 files
// can be applied.
export type OepDocumentDocType = 'abp-4' | 'abp-5'

export interface OepDocumentListItemDto {
  readonly documentId: string
  readonly filename: string
  readonly version: number
  readonly fileSizeBytes: number | null
  readonly uploadedBy: string | null
  readonly uploadedAt: string
  // Whether this document is the currently-applied one for its plan year
  // (EMS-10185) — distinct from OepApplyResultDto's `status: 'applied'` string.
  readonly status: boolean
  // Resolved in bulk by the backend for the whole list (EMS-10154,
  // 2026-09-08) — avoids a separate GET .../processing-status poll per row
  // just to learn which rows even have a pending job.
  readonly processingStatus: OepDocumentProcessingStatus
  // ISO 8601 timestamp when the analysis_file_uploaded job reached a
  // terminal state (broker_background_jobs.completed_at), or null if no job
  // has completed for this document yet (EMS-10185).
  readonly completedAt: string | null
  // Why the analysis_file_uploaded job failed — set only when
  // processingStatus is 'failed' and the job stored a message; already
  // cleaned up server-side (no raw SQL/S3 keys). Shown as the "Fail" tag's
  // tooltip (EMS-10551).
  readonly failureReason: string | null
  // The problems upload validation found, or null unless the file failed
  // validation — backs the "More details" modal next to failureReason.
  readonly validationIssues: AnalysisFileValidationIssuesDto | null
  // Raw S3 key of the generated contribution file, or null if no
  // contribution model has been generated yet for this document (2026-09-09).
  // Not a usable URL on its own — presign via getContributionFileDownloadUrl.
  readonly contributionFileUrl: string | null
  // abp_contribution_model.use_current_model — whether the generated
  // contribution model reused the client's current model as its starting
  // point, or null if no contribution model has been generated yet
  // (EMS-10154, 2026-09-14).
  readonly useCurrentModel: boolean | null
  // abp_contribution_model.adjustments_made — whether manual adjustments
  // were applied on top of the generated contribution model, or null if no
  // contribution model has been generated yet (EMS-10154, 2026-09-14).
  readonly adjustmentsMade: boolean | null
  // Whether contribution-model generation wrote an error workbook. The
  // storage key stays on the server — presign via getErrorFileDownloadUrl.
  readonly hasErrorFile: boolean
  // Human-readable summary of contribution-model generation errors, or
  // null when generation succeeded without errors.
  readonly errorMessage: string | null
  // The backend's one "generated with errors" rule (non-empty error file or
  // error message) — the same rule its retry endpoint uses (EMS-11093).
  // False when absent; there is no fallback to hasErrorFile/errorMessage.
  readonly hasGenerationErrors: boolean
  // null until the backend returns a doc type for the row (EMS-11022).
  readonly docType: OepDocumentDocType | null
  // Latest analysis_file_uploaded job for the document — the ABP dashboard
  // retries it when the contribution model has errors (EMS-11093). Null when
  // there is no job, and against a backend that predates the field.
  readonly analysisJobId: string | null
}

export interface OepApplyResultDto {
  readonly documentId: string
  readonly status: 'applied'
  readonly appliedAt: string
}

export interface OepDocumentProcessingStatusDto {
  readonly documentId: string
  readonly processingStatus: OepDocumentProcessingStatus
}

export interface OepDocumentDownloadDto {
  readonly presignedUrl: string
  readonly filename: string
}

/** list returns { documents: [{ documentId, filename, version, ... }] } — [] is a normal "no files yet" result. */
const ListDocumentsSchema = withEnvelope(
  z.object({
    documents: z.array(
      z.object({
        documentId: z.string().min(1),
        filename: z.string().min(1),
        version: z.number(),
        fileSizeBytes: z.number().nullable().catch(null),
        uploadedBy: z.string().nullable().catch(null),
        uploadedAt: z.string().min(1),
        status: z.boolean().catch(false),
        // Fail open ("completed") for a stale FE build talking to a backend
        // that hasn't shipped this field yet, rather than reject the row.
        processingStatus: z
          .enum(['processing', 'completed', 'failed'])
          .catch('completed'),
        completedAt: z.string().nullable().catch(null),
        failureReason: z.string().nullable().catch(null),
        validationIssues: z
          .object({
            totalCount: z.number(),
            issues: z.array(
              z.object({
                sheet: z.string(),
                row: z.number().nullable(),
                column: z.string().nullable(),
                message: z.string(),
              })
            ),
          })
          .nullable()
          .catch(null),
        contributionFileUrl: z.string().nullable().catch(null),
        useCurrentModel: z.boolean().nullable().catch(null),
        adjustmentsMade: z.boolean().nullable().catch(null),
        hasErrorFile: z.boolean().catch(false),
        errorMessage: z.string().nullable().catch(null),
        hasGenerationErrors: z.boolean().catch(false),
        docType: z.enum(['abp-4', 'abp-5']).nullable().catch(null),
        analysisJobId: z.string().min(1).nullable().catch(null),
      })
    ),
  })
)

/** apply returns { documentId, status: 'applied', appliedAt } — persisted server-side since EMS-10185 (see oep_documents.status, exposed on the list endpoint's OepDocumentListItemDto.status). */
const ApplyResultSchema = withEnvelope(
  z.object({
    documentId: z.string().min(1),
    status: z.literal('applied'),
    appliedAt: z.string().min(1),
  })
)

/** processing-status returns { documentId, processingStatus }. */
const ProcessingStatusSchema = withEnvelope(
  z.object({
    documentId: z.string().min(1),
    processingStatus: z.enum(['processing', 'completed', 'failed']),
  })
)

/** :documentId/download-url returns { presignedUrl, filename, ... } — one specific document, not the latest. */
const DocumentDownloadSchema = withEnvelope(
  z.object({
    presignedUrl: z.string().min(1),
    filename: z.string().min(1),
  })
)

/**
 * One row of the popup grid, in the shape the AnalysisFileDetailModal column
 * defs (and the page-local ParsedContributionRow type, OpenEnrollment/types.ts)
 * already expect — PascalCase-named string fields, verbatim from the legacy
 * ContributionTab.tsx grid.
 */
export interface OepDocumentDetailRowDto {
  readonly [key: string]: string
  readonly Relationship: string
  readonly ContributionAmountOrPercentage: string
  readonly PriceMetric: string
  readonly MetalType: string
  readonly MinAge: string
  readonly MaxAge: string
  readonly State: string
  readonly RatingArea: string
  readonly EmploymentStatus: string
  readonly ExemptStatus: string
  readonly ContributionGroup: string
  readonly PlanYear: string
  readonly BenefitType: string
  readonly ContributionType: string
}

/**
 * A single rule as returned by the real (non-mocked) backend endpoint,
 * GET api/annual-benefit-planning/v2/contribution-model/by-document/:oepDocumentId
 * (abp-4-contribution-modeling module's ContributionModelRuleView) — camelCase
 * fields, numeric where the backend stores a number.
 */
const ContributionModelRuleSchema = z.object({
  planYear: z.number(),
  minAge: z.number(),
  maxAge: z.number(),
  amount: z.number(),
  // Null / missing maps to 'UNSPECIFIED' in the grid.
  state: z.string().nullable(),
  // Backend may send a number, the literal 'UNSPECIFIED', or null/NaN for a
  // missing rating area. Accept all so older and newer payloads both parse;
  // null / NaN map to 'UNSPECIFIED' in the grid.
  ratingArea: z.union([
    z.number(),
    z.literal('UNSPECIFIED'),
    z.nan(),
    z.null(),
  ]),
  relationship: z.string(),
  benefitType: z.string(),
  employmentStatus: z.string(),
  exemptStatus: z.string(),
  interval: z.string(),
  contributionType: z.string(),
  // Null maps to blank in the grid.
  priceMetric: z.string().nullable(),
  metalTier: z.string().nullable(),
  contributionGroup: z.string(),
})

/**
 * contribution-model/by-document/:oepDocumentId returns the ContributionModel
 * itself (no { status, data } envelope — this controller doesn't wrap its
 * response), with the rules this popup renders nested under `rules`.
 */
const ContributionModelSchema = z.object({
  id: z.string(),
  oepDocumentId: z.string(),
  clientPublicKey: z.string(),
  planYear: z.number(),
  contributionFileUrl: z.string().nullable(),
  createdAt: z.string(),
  rules: z.array(ContributionModelRuleSchema),
})

/** GET a presigned S3 URL + upload key for a new analysis document. */
export async function getOepDocumentUploadUrl(
  clientPublicKey: string,
  oepYear: number,
  filename: string
): Promise<OepPresignDto> {
  const { data } = await annualBenefitPlanningApi.get(
    `clients/${encodeURIComponent(clientPublicKey)}/analysis-documents/upload-url`,
    { params: { oepYear, filename } }
  )
  const parsed = PresignSchema.safeParse(data)
  if (!parsed.success) {
    throw new Error('Invalid presign response from server.')
  }
  return parsed.data
}

/** POST to confirm an uploaded object as the analysis document for the year. */
export async function confirmOepDocument(
  clientPublicKey: string,
  body: { oepYear: number; uploadKey: string; filename: string }
): Promise<OepDocumentDto> {
  const { data } = await annualBenefitPlanningApi.post(
    `clients/${encodeURIComponent(clientPublicKey)}/analysis-documents/confirm`,
    body
  )
  const parsed = ConfirmDocumentSchema.safeParse(data)
  if (!parsed.success) {
    throw new Error('Unexpected response shape from confirm endpoint.')
  }
  return parsed.data
}

/** DELETE the analysis document by id. */
export async function deleteOepDocument(
  clientPublicKey: string,
  documentId: string
): Promise<void> {
  await annualBenefitPlanningApi.delete(
    `clients/${encodeURIComponent(clientPublicKey)}/analysis-documents/${encodeURIComponent(
      documentId
    )}`
  )
}

/**
 * GET every non-deleted analysis document for one OEP, newest first. Without
 * `oepId` EPS lists the client's current OEP (EMS-10573). `documents: []` is a
 * normal result ("no files uploaded yet"), not an error — unlike the
 * single-document download-url lookup above, this endpoint never 404s for
 * "no documents" (it 404s only for an oepId that isn't the client's).
 */
export async function listOepDocuments(
  clientPublicKey: string,
  oepId?: string
): Promise<OepDocumentListItemDto[]> {
  const { data } = await annualBenefitPlanningApi.get(
    `clients/${encodeURIComponent(clientPublicKey)}/analysis-documents`,
    { params: { oepId } }
  )
  const parsed = ListDocumentsSchema.safeParse(data)
  if (!parsed.success) {
    throw new Error(
      'Unexpected response shape from the document list endpoint.'
    )
  }
  return parsed.data.documents
}

/**
 * POST to apply an analysis document — makes it the current file for its plan
 * year (oep_documents.status) and clears the flag on the others.
 */
export async function applyOepDocument(
  clientPublicKey: string,
  documentId: string
): Promise<OepApplyResultDto> {
  const { data } = await annualBenefitPlanningApi.post(
    `clients/${encodeURIComponent(clientPublicKey)}/analysis-documents/${encodeURIComponent(
      documentId
    )}/apply`
  )
  const parsed = ApplyResultSchema.safeParse(data)
  if (!parsed.success) {
    throw new Error('Unexpected response shape from the apply endpoint.')
  }
  return parsed.data
}

/**
 * GET the processing status of an analysis document upload (EMS-10185 User
 * Story 2) — derived from the broker_background_jobs ledger. Polled by
 * useOepDocumentProcessingStatus every 60s while 'processing'.
 */
export async function getOepDocumentProcessingStatus(
  clientPublicKey: string,
  documentId: string
): Promise<OepDocumentProcessingStatusDto> {
  const { data } = await annualBenefitPlanningApi.get(
    `clients/${encodeURIComponent(clientPublicKey)}/analysis-documents/${encodeURIComponent(
      documentId
    )}/processing-status`
  )
  const parsed = ProcessingStatusSchema.safeParse(data)
  if (!parsed.success) {
    throw new Error(
      'Unexpected response shape from the processing-status endpoint.'
    )
  }
  return parsed.data
}

const OepTargetFileJobSchema = z.object({
  id: z.string().min(1),
  fileName: z.string().min(1),
  status: z.enum(['QUEUED', 'PROCESSING', 'COMPLETED', 'FAILED']),
  requestedAt: z.string().min(1),
  // Display name of whoever pressed Generate. Null for rows written before the
  // actor was recorded, and against a backend that predates this field.
  requestedBy: z.string().min(1).nullable().catch(null),
  generatedAt: z.string().nullable(),
  // Analysis document this ABP-5 job was generated from. Absent on ABP-4
  // target-file jobs and on responses from before the field existed.
  documentId: z.string().min(1).nullish(),
})

const LatestOepTargetFileJobSchema = withEnvelope(
  z.union([OepTargetFileJobSchema, z.null()])
)

const OepTargetFileDownloadUrlSchema = withEnvelope(
  z.object({
    presignedUrl: z.string().min(1),
    filename: z.string().min(1),
    expiresAt: z.string().min(1),
  })
)

/**
 * The two generated-workbook job resources on EPS share one response shape:
 * the ABP-4 target file (`oep-target-file/jobs`) and the ABP-5 file
 * (`abp5-file/jobs`).
 */
type GeneratedWorkbookJobsResource = 'oep-target-file' | 'abp5-file'

function generatedWorkbookJobsPath(
  clientPublicKey: string,
  resource: GeneratedWorkbookJobsResource
): string {
  return `clients/${encodeURIComponent(clientPublicKey)}/${resource}/jobs`
}

/**
 * A 404 means "no job generated yet" (→ null, shown as the empty jobs table),
 * not a load error. Any other failure (5xx, network) is re-thrown.
 */
async function getLatestGeneratedWorkbookJob(
  clientPublicKey: string,
  resource: GeneratedWorkbookJobsResource
): Promise<OepTargetFileJob | null> {
  try {
    const { data } = await annualBenefitPlanningApi.get(
      generatedWorkbookJobsPath(clientPublicKey, resource)
    )
    const parsed = LatestOepTargetFileJobSchema.safeParse(data)
    if (!parsed.success) {
      throw new Error(`Unexpected response shape from ${resource}/jobs GET.`)
    }
    return parsed.data
  } catch (error: unknown) {
    const response =
      isRecord(error) && isRecord(error.response) ? error.response : null
    if (isRecord(response) && response.status === 404) {
      return null
    }
    throw error
  }
}

async function postGenerateWorkbookJob(
  clientPublicKey: string,
  resource: GeneratedWorkbookJobsResource,
  body?: { readonly documentId: string }
): Promise<OepTargetFileJob> {
  const path = generatedWorkbookJobsPath(clientPublicKey, resource)
  const { data } =
    body === undefined
      ? await annualBenefitPlanningApi.post(path)
      : await annualBenefitPlanningApi.post(path, body)
  const parsed = withEnvelope(OepTargetFileJobSchema).safeParse(data)
  if (!parsed.success) {
    throw new Error(`Unexpected response shape from ${resource}/jobs POST.`)
  }
  return parsed.data
}

async function getGeneratedWorkbookDownloadUrl(
  clientPublicKey: string,
  resource: GeneratedWorkbookJobsResource,
  jobId: string
): Promise<{ presignedUrl: string; filename: string; expiresAt: string }> {
  const { data } = await annualBenefitPlanningApi.get(
    `${generatedWorkbookJobsPath(clientPublicKey, resource)}/${encodeURIComponent(jobId)}/download-url`
  )
  const parsed = OepTargetFileDownloadUrlSchema.safeParse(data)
  if (!parsed.success) {
    throw new Error(
      `Unexpected response shape from ${resource}/jobs download-url.`
    )
  }
  return parsed.data
}

/** GET the latest OEP target file (ABP-4) generation job, or null when none exist. */
export async function getLatestOepTargetFileJob(
  clientPublicKey: string
): Promise<OepTargetFileJob | null> {
  return getLatestGeneratedWorkbookJob(clientPublicKey, 'oep-target-file')
}

/** POST to enqueue a new OEP target file (ABP-4) generation job for the client. */
export async function postGenerateOepTargetFile(
  clientPublicKey: string
): Promise<OepTargetFileJob> {
  return postGenerateWorkbookJob(clientPublicKey, 'oep-target-file')
}

/** GET the latest ABP-5 file generation job, or null when none exist. */
export async function getLatestAbp5FileJob(
  clientPublicKey: string
): Promise<OepTargetFileJob | null> {
  return getLatestGeneratedWorkbookJob(clientPublicKey, 'abp5-file')
}

/**
 * Latest ABP-5 job per analysis document — one entry per `documentId`.
 * Used by the Analysis Upload table so each row can show its own file vs Generate.
 */
const Abp5FileJobByDocumentSchema = OepTargetFileJobSchema.extend({
  documentId: z.string().min(1),
  // EMS-11070: the generated file's own document id (null until COMPLETED)
  // and whether it is current. Missing on an older backend → null / false.
  generatedDocumentId: z.string().min(1).nullable().catch(null),
  isCurrent: z.boolean().catch(false),
})

const Abp5FileJobsByDocumentSchema = withEnvelope(
  z.object({
    jobs: z.array(Abp5FileJobByDocumentSchema),
  })
)

/**
 * GET the latest ABP-5 job for each analysis document on the client.
 * Empty list when none exist (200 + `jobs: []`), not 404.
 */
export async function getAbp5FileJobsByDocument(
  clientPublicKey: string
): Promise<readonly OepTargetFileJob[]> {
  const { data } = await annualBenefitPlanningApi.get(
    `${generatedWorkbookJobsPath(clientPublicKey, 'abp5-file')}/by-document`
  )
  const parsed = Abp5FileJobsByDocumentSchema.safeParse(data)
  if (!parsed.success) {
    throw new Error(
      'Unexpected response shape from abp5-file/jobs/by-document GET.'
    )
  }
  return parsed.data.jobs
}

/**
 * POST to enqueue ABP-5 file generation for one analysis document.
 * `documentId` tells EPS which uploaded analysis file to fill.
 * EPS answers 422 when that file is not available.
 */
export async function postGenerateAbp5File(
  clientPublicKey: string,
  documentId: string
): Promise<OepTargetFileJob> {
  const job = await postGenerateWorkbookJob(clientPublicKey, 'abp5-file', {
    documentId,
  })
  return { ...job, documentId: job.documentId ?? documentId }
}

/** GET a presigned download URL for a completed ABP-5 file job. */
export async function getAbp5FileDownloadUrl(
  clientPublicKey: string,
  jobId: string
): Promise<{ presignedUrl: string; filename: string; expiresAt: string }> {
  return getGeneratedWorkbookDownloadUrl(clientPublicKey, 'abp5-file', jobId)
}

/**
 * GET a presigned S3 URL to download one specific analysis document by id —
 * distinct from the year-scoped download-url above, which always returns
 * the currently-applied (status: true) document, not merely the most
 * recently uploaded one. Needed because multiple non-deleted documents can
 * now coexist per plan year (EMS-10154 FR-003 supersession).
 */
export async function getOepDocumentDownloadUrl(
  clientPublicKey: string,
  documentId: string
): Promise<OepDocumentDownloadDto> {
  const { data } = await annualBenefitPlanningApi.get(
    `clients/${encodeURIComponent(clientPublicKey)}/analysis-documents/${encodeURIComponent(
      documentId
    )}/download-url`
  )
  const parsed = DocumentDownloadSchema.safeParse(data)
  if (!parsed.success) {
    throw new Error(
      'Unexpected response shape from the document download-url endpoint.'
    )
  }
  return parsed.data
}

/**
 * GET a presigned S3 URL to download the generated contribution file for an
 * analysis document (2026-09-09). Only meaningful once
 * OepDocumentListItemDto.contributionFileUrl is non-null for the row — the
 * backend 404s otherwise (no contribution model generated yet).
 */
export async function getContributionFileDownloadUrl(
  clientPublicKey: string,
  documentId: string
): Promise<OepDocumentDownloadDto> {
  const { data } = await annualBenefitPlanningApi.get(
    `clients/${encodeURIComponent(clientPublicKey)}/analysis-documents/${encodeURIComponent(
      documentId
    )}/contribution-file-download-url`
  )
  const parsed = DocumentDownloadSchema.safeParse(data)
  if (!parsed.success) {
    throw new Error(
      'Unexpected response shape from the contribution-file-download-url endpoint.'
    )
  }
  return parsed.data
}

/**
 * GET a presigned S3 URL to download the error log file for an analysis
 * document. Only meaningful once OepDocumentListItemDto.hasErrorFile is
 * true for the row — the backend 404s otherwise (no error file).
 */
export async function getErrorFileDownloadUrl(
  clientPublicKey: string,
  documentId: string
): Promise<OepDocumentDownloadDto> {
  const { data } = await annualBenefitPlanningApi.get(
    `clients/${encodeURIComponent(clientPublicKey)}/analysis-documents/${encodeURIComponent(
      documentId
    )}/error-file-download-url`
  )
  const parsed = DocumentDownloadSchema.safeParse(data)
  if (!parsed.success) {
    throw new Error(
      'Unexpected response shape from the error-file-download-url endpoint.'
    )
  }
  return parsed.data
}

/** GET a presigned download URL for a completed OEP target file job. */
export async function getOepTargetFileDownloadUrl(
  clientPublicKey: string,
  jobId: string
): Promise<{ presignedUrl: string; filename: string; expiresAt: string }> {
  return getGeneratedWorkbookDownloadUrl(
    clientPublicKey,
    'oep-target-file',
    jobId
  )
}

/** Maps one real ContributionModelRuleView to the popup grid's PascalCase-string row shape. */
function toParsedContributionRow(
  rule: z.infer<typeof ContributionModelRuleSchema>
): OepDocumentDetailRowDto {
  return {
    Relationship: rule.relationship,
    ContributionAmountOrPercentage: String(rule.amount),
    PriceMetric: rule.priceMetric ?? '',
    MetalType: rule.metalTier ?? '',
    MinAge: String(rule.minAge),
    MaxAge: String(rule.maxAge),
    State: rule.state ?? 'UNSPECIFIED',
    RatingArea:
      rule.ratingArea === null || Number.isNaN(rule.ratingArea)
        ? 'UNSPECIFIED'
        : String(rule.ratingArea),
    EmploymentStatus: rule.employmentStatus,
    ExemptStatus: rule.exemptStatus,
    ContributionGroup: rule.contributionGroup,
    PlanYear: String(rule.planYear),
    BenefitType: rule.benefitType,
    ContributionType: rule.contributionType,
  }
}

/**
 * GET the generated contribution model's rules for one analysis document —
 * the real, non-mocked data source (abp-4-contribution-modeling module),
 * replacing the earlier :documentId/details mock. Throws (surfaced by the
 * caller as isError) on a 404, meaning no contribution model has been
 * generated yet for this document.
 */
export async function getOepDocumentDetails(
  documentId: string
): Promise<readonly OepDocumentDetailRowDto[]> {
  const { data } = await contributionModelApi.get(
    `contribution-model/by-document/${encodeURIComponent(documentId)}`
  )
  const parsed = ContributionModelSchema.safeParse(data)
  if (!parsed.success) {
    throw new Error(
      'Unexpected response shape from the contribution-model by-document endpoint.'
    )
  }
  return parsed.data.rules.map(toParsedContributionRow)
}
